import { useEffect, useMemo, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { languageLabel, languageLabelWithCode } from '@/shared/lib/language'
import { explain } from '@/shared/api/problem'
import type { SurveyInstrumentDto, SurveyQuestionDto } from '@/shared/api/types'
import { setOptionTranslation, setQuestionTranslation } from './api'
import { PublishedOffer, RetargetedNote, publishedRefusal, type VersionOffer } from './QuestionDialog'
import { Pill } from './vocab'

/**
 * One question in one other language, and the options that go with it.
 *
 * ## Why the options are here and not in a second dialog
 *
 * A question translated without its card is worse than one not translated at
 * all: the interviewer reads the Kiswahili question and then the English answer
 * list, and every respondent who picks by position rather than by meaning is
 * mismeasured in a way nothing downstream can detect. So the two writes are one
 * form, and the coverage line counts both.
 *
 * ## Two endpoints, two hundred and four
 *
 * `PUT …/translations` returns 204 on both routes, so nothing comes back to take
 * as the new state. The page re-reads the instrument once the dialog is done —
 * which is also how a newly used language appears in the instrument's language
 * list without this file guessing that it will.
 */
export function TranslationDialog({
  open, versionId, question, instrument, offer, onClose, onSaved,
}: {
  open: boolean
  versionId: string
  question: SurveyQuestionDto
  instrument: SurveyInstrumentDto
  offer: VersionOffer
  onClose: () => void
  /** 204 on every route here — the page re-reads. */
  onSaved: () => void
}) {
  const [languageCode, setLanguageCode] = useState('')
  const [newLanguage, setNewLanguage] = useState('')
  const [text, setText] = useState('')
  const [helpText, setHelpText] = useState('')
  const [optionText, setOptionText] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<{ sentence: string; versionId: string } | null>(null)

  const languages = instrument.languages
  const missing = useMemo(
    () => languages.filter((l) => !question.translations[l]), [languages, question.translations])

  useEffect(() => {
    if (!open) return
    setLanguageCode(missing[0] ?? languages[0] ?? '__new')
    setNewLanguage('')
    setError(null)
    setSaved(null)
    setRefusal(null)
    // Deliberately not keyed on `question`: the page hands back a fresh object
    // after each save, and resetting on that would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const language = languageCode === '__new' ? newLanguage.trim() : languageCode

  /** Load whatever is already on file for this language, so a save is an edit and not a wipe. */
  useEffect(() => {
    if (!open) return
    const existing = question.translations[language]
    setText(existing?.text ?? '')
    setHelpText(existing?.helpText ?? '')
    const seed: Record<string, string> = {}
    for (const o of question.options) seed[o.id] = o.translations[language] ?? ''
    setOptionText(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, language])

  const movedOn = refusal !== null && refusal.versionId !== versionId

  async function submit() {
    setBusy(true)
    setError(null)
    setSaved(null)
    setRefusal(null)
    let written = 0
    try {
      await setQuestionTranslation(question.id, {
        languageCode: language,
        text: text.trim(),
        helpText: helpText.trim() || undefined,
      })
      written += 1
      // Sequential, not parallel: a failure halfway through leaves a count that
      // says how far it got, and `Promise.all` would leave a set of writes that
      // may or may not have landed with no way to say which.
      for (const option of question.options) {
        const value = (optionText[option.id] ?? '').trim()
        // An empty box is "not translated yet", not "translate it to nothing" —
        // the server refuses a blank, and sending one would fail the whole save
        // over an option nobody had got to.
        if (value === '' || value === (option.translations[language] ?? '')) continue
        await setOptionTranslation(option.id, { languageCode: language, text: value })
        written += 1
      }
      setSaved(`${written} write${written === 1 ? '' : 's'} in ${language}`)
      onSaved()
    } catch (e) {
      const frozen = publishedRefusal(e)
      if (frozen) setRefusal({ sentence: frozen, versionId })
      else {
        setError(written === 0 ? explain(e)
          : `${explain(e)} The first ${written} of these writes did land — reopen this to see `
            + 'what is stored.')
      }
      // Whatever landed before the failure is real, so the page re-reads either way.
      if (written > 0) onSaved()
    } finally {
      setBusy(false)
    }
  }

  const translatedOptions = question.options
    .filter((o) => (o.translations[language] ?? '') !== '').length

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={`Translate ${question.code}`}
      description="The words as they will be read out in this language, and the answers as they will be offered."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Close
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : `Save ${language || 'translation'}`}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {refusal && !movedOn && <PublishedOffer sentence={refusal.sentence} offer={offer} />}
        {movedOn && <RetargetedNote versionNo={offer.nextVersionNo} keyedToARow />}
        {saved && <Note tone="good" onDismiss={() => setSaved(null)}>Saved — {saved}.</Note>}

        <div className="rounded border border-border bg-muted/20 p-2.5 space-y-1.5">
          <p className="text-erp-sm">
            <span className="font-mono text-erp-xs text-muted-foreground mr-2">
              {question.code}
            </span>
            {question.text}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 text-erp-sm">
            <span className="text-muted-foreground text-erp-xs">Translated into:</span>
            {Object.keys(question.translations).length === 0
              ? <span className="text-erp-xs text-muted-foreground">nothing yet</span>
              : Object.keys(question.translations).map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
            {missing.length > 0 && (
              <>
                <span className="text-muted-foreground text-erp-xs ml-2">Still missing:</span>
                {missing.map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Language" required
              hint="From the languages this instrument already carries. A new one is added by writing the first translation in it.">
              <select className="erp-input h-9 w-full" value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}>
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {/* The code stays visible here, unlike everywhere else: this dialog is
                        where somebody TYPES a code for a new language, so both halves of
                        the list have to be comparable. */}
                    {languageLabelWithCode(l)}
                    {question.translations[l] ? ' — translated' : ' — missing'}
                  </option>
                ))}
                <option value="__new">Another language…</option>
              </select>
            </Field>
          </div>
          {languageCode === '__new' && (
            <div className="col-span-3">
              <Field label="Code" required hint="At most 8 characters, e.g. sw.">
                <input className="erp-input h-9 w-full font-mono text-erp-sm" maxLength={8}
                  value={newLanguage} placeholder="sw"
                  onChange={(e) => setNewLanguage(e.target.value)} />
              </Field>
            </div>
          )}
          <div className="col-span-4 self-center pt-3">
            <p className="text-erp-xs text-muted-foreground">
              {question.options.length === 0
                ? 'This question has no options to translate.'
                : `${translatedOptions} of ${question.options.length} options are translated into `
                  + `${language || 'this language'}.`}
            </p>
          </div>
        </div>

        <Field label="Question text" required hint="What the interviewer reads out.">
          <textarea className="erp-input w-full min-h-[64px] py-2" value={text} maxLength={4000}
            onChange={(e) => setText(e.target.value)} />
        </Field>

        <Field label="Help text"
          hint="Optional. The interviewer's instructions, in the language they are working in.">
          <textarea className="erp-input w-full min-h-[48px] py-2" value={helpText} maxLength={4000}
            onChange={(e) => setHelpText(e.target.value)} />
        </Field>

        {question.options.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-erp-sm font-medium">Options</p>
            <p className="text-erp-xs text-muted-foreground">
              Each is saved separately and only when it changes. An empty box is left alone rather
              than written as a blank — the server refuses those, and one untranslated option
              should not cost the rest of the save.
            </p>
            <div className="space-y-1.5 pt-1">
              {question.options.map((o) => (
                <div key={o.id} className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-2 font-mono text-erp-xs text-muted-foreground truncate">
                    {o.code}
                  </span>
                  <span className="col-span-4 text-erp-sm truncate">{o.label}</span>
                  <input className="col-span-6 erp-input h-8 w-full text-erp-sm" maxLength={300}
                    aria-label={`${o.code} in ${language || 'the chosen language'}`}
                    value={optionText[o.id] ?? ''}
                    placeholder={o.translations[language] ? '' : 'not translated'}
                    onChange={(e) => setOptionText((prev) => ({
                      ...prev, [o.id]: e.target.value,
                    }))} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
